/**
 * The part of `tesseract.js` the OCR helpers (ocr.ts) use, and its loader.
 *
 * `tesseract.js` is an OPTIONAL peer of @gotti/test-utils — 30 MB with its engine, installed only by a project that
 * reads text off the screen. So nothing here names its types: these declarations are part of the package's published
 * types, and a project without the peer must still type-check them. The shapes below are the ones tesseract.js 5
 * returns and takes; `loadTesseract` hands its module out typed by them.
 */

export interface TesseractBoundingBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface TesseractSegment {
  text?: string;
  confidence?: number;
  bbox?: TesseractBoundingBox;
  box?: TesseractBoundingBox;
}

export interface RecognizeData {
  text: string;
  words?: TesseractSegment[];
  lines?: TesseractSegment[];
  paragraphs?: TesseractSegment[];
}

export interface RecognizeResult {
  data: RecognizeData;
}

export type TesseractImage = string | Buffer | ArrayBuffer | Uint8Array;

/**
 * Tesseract ENGINE parameters — `tessedit_char_whitelist`,
 * `tessedit_pageseg_mode`, and friends. These reach the engine ONLY through
 * `createWorker`'s fourth (config) argument.
 */
export interface WorkerParams {
  [key: string]: unknown;
}

/**
 * Worker-construction options (corePath, langPath, logger…). Deliberately
 * NOT an open key map: this slot silently ignores engine parameters, and an
 * open map is what let `tessedit_char_whitelist` be passed here for a long
 * time with no error and no effect.
 */
export interface WorkerOptions {
  corePath?: string;
  langPath?: string;
  cachePath?: string;
  logger?: (arg: unknown) => void;
  errorHandler?: (arg: unknown) => void;
}

export interface Worker {
  recognize(image: TesseractImage): Promise<RecognizeResult>;
  setParameters(params: Partial<WorkerParams>): Promise<unknown>;
  terminate(): Promise<unknown>;
}

export interface TesseractModule {
  /**
   * NOTE: there is intentionally no engine-parameter slot here. The real
   * `recognize(image, langs, options)` forwards `options` to
   * `createWorker(langs, oem, options)` as WORKER options, so engine
   * parameters passed to it are dropped without any error. Use
   * `createWorker(..., config)` when you need parameters.
   */
  recognize(
    image: TesseractImage,
    languages?: string,
    options?: WorkerOptions
  ): Promise<RecognizeResult>;
  createWorker(
    langs?: string,
    oem?: number,
    options?: WorkerOptions,
    config?: Partial<WorkerParams>
  ): Promise<Worker>;
}

/**
 * The project's own `tesseract.js`, imported when the OCR helpers first recognise text — never when this package loads —
 * through a specifier held in a variable, so no bundler resolves it statically: a page that loads this package (the
 * playground aliases it to its full entry) neither pulls the engine in nor fails without it. Rejects naming the peer
 * when it cannot be loaded, with the import failure as `cause`.
 */
export async function loadTesseract(): Promise<TesseractModule> {
  const spec = 'tesseract.js';
  try {
    // tesseract.js is CommonJS: its exports object is the namespace's `default`.
    const module = await import(/* @vite-ignore */ spec);
    return (module.default ?? module) as TesseractModule;
  } catch (cause) {
    throw new Error('@gotti/test-utils needs the optional peer "tesseract.js" to read text off the screen: npm install -D tesseract.js', { cause });
  }
}
